import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/corporation/api';
import PageHeader from '@components/common/PageHeader';
import { useForm } from 'react-hook-form';
import {
    toastSuccess,
    toastWarning,
} from '@components/common/Toast/CustomToast';
import { Alert } from '@utils/alert';
import ActionButton from '@/components/common/ActionButton/ActionButton';
import { CorporationAddReq } from '@/api/corporation/type';

const CorporationAdd = () => {
    const navigate = useNavigate();

    const {
        register,
        getValues,
        formState: { errors },
        trigger,
    } = useForm();

    // 상수 정의
    const JOIN_TYPE = {
        NAVER: 0,
        KAKAO: 1,
        GOOGLE: 2,
        ID: 9,
    } as const;

    const JOIN_TYPE_LABELS = {
        [JOIN_TYPE.NAVER]: '네이버',
        [JOIN_TYPE.KAKAO]: '카카오',
        [JOIN_TYPE.GOOGLE]: '구글',
        [JOIN_TYPE.ID]: 'ID',
    } as const;

    const JOIN_TYPE_OPTIONS = [
        { value: JOIN_TYPE.NAVER, label: JOIN_TYPE_LABELS[JOIN_TYPE.NAVER] },
        { value: JOIN_TYPE.KAKAO, label: JOIN_TYPE_LABELS[JOIN_TYPE.KAKAO] },
        { value: JOIN_TYPE.GOOGLE, label: JOIN_TYPE_LABELS[JOIN_TYPE.GOOGLE] },
        { value: JOIN_TYPE.ID, label: JOIN_TYPE_LABELS[JOIN_TYPE.ID] },
    ];

    const USER_STATUS = {
        NORMAL: 0,
        SUSPENDED: 1,
        WITHDRAWN: 2,
        DELETED: 3,
    } as const;

    const USER_STATUS_LABELS = {
        [USER_STATUS.NORMAL]: '정상',
        [USER_STATUS.SUSPENDED]: '정지',
        [USER_STATUS.WITHDRAWN]: '탈퇴',
        [USER_STATUS.DELETED]: '삭제',
    } as const;

    const USER_STATUS_OPTIONS = [
        {
            value: USER_STATUS.NORMAL,
            label: USER_STATUS_LABELS[USER_STATUS.NORMAL],
        },
        {
            value: USER_STATUS.SUSPENDED,
            label: USER_STATUS_LABELS[USER_STATUS.SUSPENDED],
        },
        {
            value: USER_STATUS.WITHDRAWN,
            label: USER_STATUS_LABELS[USER_STATUS.WITHDRAWN],
        },
        {
            value: USER_STATUS.DELETED,
            label: USER_STATUS_LABELS[USER_STATUS.DELETED],
        },
    ];

    const [formData, setFormData] = useState({
        joinType: JOIN_TYPE.NAVER as number,
        userStatus: USER_STATUS.NORMAL as number,
        userType: 0,
        userId: '',
        userName: '',
        phone: '',
        email: '',
        address: '',
        password: '',
    });

    const handleInputChange = (field: string, value: string | number) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleJoinTypeChange = (joinType: number) => {
        handleInputChange('joinType', joinType);
    };

    const handleUserStatusChange = (userStatus: number) => {
        handleInputChange('userStatus', userStatus);
    };

    // 유효성 검사
    const validateForm = async () => {
        // 필수 필드 검사
        if (!formData.userName.trim()) {
            toastWarning('대행사명을 입력해주세요.');
            return false;
        }
        if (!formData.userId.trim()) {
            toastWarning('아이디를 입력해주세요.');
            return false;
        }
        if (!formData.phone.trim()) {
            toastWarning('전화번호를 입력해주세요.');
            return false;
        }
        if (!formData.email.trim()) {
            toastWarning('이메일을 입력해주세요.');
            return false;
        }

        // 비밀번호 유효성 검사
        const passwordValue = getValues('password');
        if (!passwordValue) {
            toastWarning('비밀번호를 입력해주세요.');
            return false;
        }

        const isPasswordValid = await trigger('password');
        if (!isPasswordValid) {
            toastWarning('비밀번호 입력 조건을 확인해주세요.');
            return false;
        }

        // 이메일 유효성 검사
        const isEmailValid = await trigger('email');
        if (!isEmailValid) {
            toastWarning('올바른 이메일 주소를 입력해주세요.');
            return false;
        }

        return true;
    };

    // 등록 핸들러
    const handleSave = async () => {
        // 유효성 검사
        const isValid = await validateForm();
        if (!isValid) return;

        await Alert.confirmAndExecute(
            '대행사를 등록하시겠습니까?',
            '입력한 정보를 다시 한번 확인해주세요.',
            true,
            async () => {
                const passwordValue = getValues('password');
                const addRequest: CorporationAddReq = {
                    userType: formData.userType,
                    joinType: formData.joinType,
                    userId: formData.userId,
                    userPw: passwordValue,
                    userName: formData.userName,
                    phone: formData.phone,
                    email: formData.email,
                    address: formData.address,
                    userStatus: formData.userStatus,
                };

                await api.corporationAdd(addRequest).then(() => {
                    toastSuccess('대행사가 성공적으로 등록되었습니다.');
                    handleCancel();
                });
            },
        );
    };

    const handleCancel = (): void => {
        navigate('/corporation');
    };

    return (
        <>
            <PageHeader
                title="대행사 등록"
                breadcrumbs={[
                    { label: '홈', href: '/' },
                    { label: '사용자관리' },
                    { label: '대행사관리' },
                    { label: '대행사 등록', active: true },
                ]}
            />

            <div className="card custom-card mt-4">
                <div className="card-header">
                    <div className="card-title">
                        대행사 정보를 입력해주세요.
                    </div>
                </div>
                <div className="card-body">
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                대행사명<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="대행사명을 입력해주세요."
                                value={formData.userName}
                                onChange={e =>
                                    handleInputChange(
                                        'userName',
                                        e.target.value,
                                    )
                                }
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                아이디<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="아이디를 입력해주세요."
                                value={formData.userId}
                                onChange={e =>
                                    handleInputChange('userId', e.target.value)
                                }
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                비밀번호<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="password"
                                className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                placeholder="비밀번호를 입력해주세요."
                                {...register('password', {
                                    required: '비밀번호를 입력해주세요.',
                                    minLength: {
                                        value: 6,
                                        message: '6자 이상 입력해주세요',
                                    },
                                    maxLength: {
                                        value: 20,
                                        message: '20자 이하로 입력해주세요',
                                    },
                                    pattern: {
                                        value: /^(?=.*[a-zA-Z])(?=.*[0-9]).{6,20}/,
                                        message:
                                            '비밀번호는 영문과 숫자를 조합하여 6~20자 입니다.',
                                    },
                                })}
                            />
                            {errors.password && (
                                <div className="invalid-feedback d-block">
                                    {String(errors.password.message)}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                전화번호<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="tel"
                                className="form-control"
                                placeholder="전화번호를 입력해주세요."
                                value={formData.phone}
                                onChange={e =>
                                    handleInputChange('phone', e.target.value)
                                }
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                이메일<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="email"
                                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                placeholder="이메일을 입력해주세요."
                                {...register('email', {
                                    required: '이메일을 입력해주세요.',
                                    pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                        message:
                                            '올바른 이메일 주소를 입력해주세요.',
                                    },
                                    onChange: e => {
                                        handleInputChange(
                                            'email',
                                            e.target.value,
                                        );
                                    },
                                })}
                            />
                            {errors.email && (
                                <div className="invalid-feedback d-block">
                                    {String(errors.email.message)}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                주소 :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="주소를 입력해주세요."
                                value={formData.address}
                                onChange={e =>
                                    handleInputChange('address', e.target.value)
                                }
                            />
                        </div>
                    </div>

                    {/* 가입유형 - 동적 처리 */}
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                가입유형<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            {JOIN_TYPE_OPTIONS.map(option => (
                                <div
                                    key={option.value}
                                    className="form-check form-check-inline"
                                >
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="joinType"
                                        id={`joinType${option.value}`}
                                        value={option.value}
                                        checked={
                                            formData.joinType === option.value
                                        }
                                        onChange={() =>
                                            handleJoinTypeChange(option.value)
                                        }
                                    />
                                    <label
                                        className="form-check-label"
                                        htmlFor={`joinType${option.value}`}
                                    >
                                        {option.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 회원상태 - 동적 처리 */}
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                회원상태<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            {USER_STATUS_OPTIONS.map(option => (
                                <div
                                    key={option.value}
                                    className="form-check form-check-inline"
                                >
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="userStatus"
                                        id={`userStatus${option.value}`}
                                        value={option.value}
                                        checked={
                                            formData.userStatus === option.value
                                        }
                                        onChange={() =>
                                            handleUserStatusChange(option.value)
                                        }
                                    />
                                    <label
                                        className="form-check-label"
                                        htmlFor={`userStatus${option.value}`}
                                    >
                                        {option.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="card-footer">
                    <div className="btn-list text-end">
                        <ActionButton type="save" onClick={handleSave} />
                        <ActionButton type="cancel" onClick={handleCancel} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default CorporationAdd;
